import { Card, CardContent, CardHeader, Divider, Typography, ListItem, ListItemText, Pagination, Link } from "@mui/material";
import { ChangeEvent, FC, useCallback, useEffect, useState } from "react";
import { AlertIcon, AlertList, displayAlert, NoAlerts } from "../dashboard/alertCard";
import { useShowContext } from "ra-core";
import { RecPatch } from "@/dataProvider/tools";
import { chunk } from "lodash";
import { EncryptionDashboardResponseDto, AlertAndTime, LastActivityKind } from "@/gen/api/encrypt";
import { useUserTimeFormat } from "@/utils/userTimeFormat";
import { Link as RouteLink } from 'react-router-dom'
import { genUrlWithHashid } from "@/utils/hashid";

const pageSize = 3;

const EncryptionAlertsCard: FC = () => {
  const { record } = useShowContext<EncryptionDashboardResponseDto & RecPatch>()
  const alertCount = (record 
    ? (record.encryptionAlerts?.length || 0)
    : 0)

  const [page, setPage] = useState(1) // 1 based page location
  const [alertChunks, setAlertChunks] = useState<AlertAndTime[][]>([])
  useEffect(() => {
    setPage(1)
    const alertList = 
      record 
      ? (record.encryptionAlerts || []).sort((a, b) => (a.alertTime || '') > (b.alertTime || '') ? -1 : 1) 
      : []
    setAlertChunks(chunk(alertList, pageSize))
  }, [JSON.stringify(record), setPage, setAlertChunks])

  const handleChange = useCallback((e: ChangeEvent<any>, value: number) => {
    setPage(value)
  }, [setPage])

  const format = useUserTimeFormat()
  return (
    <Card>
      <CardHeader
        title={(
          <Typography
            color="textPrimary"
            variant="h6"
          >
            Alerts
          </Typography>
        )}
      />
      <CardContent sx={{ pt: 0 }} >
        <Divider />
        {alertCount > 0 ?
          <AlertList>
            {alertChunks[page - 1]?.map((a, i) => (
              <ListItem key={a.databaseId + '' + i} divider>
                <Link 
                  underline="none"
                  component={RouteLink}
                  to={
                      a.kind === LastActivityKind.EncryptionJob ? genUrlWithHashid('/encryption/list/$0/show', a.databaseId || 0)
                    : a.kind === LastActivityKind.ObfuscationJob ? genUrlWithHashid('/encryption/obfs/list/$0/show', a.databaseId || 0)
                    : { pathname: '/settings/encryption/obfuscation-rule', state: { open: (a.actionArgs || [])[0] } }
                  }
                >
                  <AlertIcon {...a} />
                  <ListItemText
                    disableTypography
                    primary={(
                      <Typography
                        className={a.isWarning ? 'warning' : 'error'}
                        variant="body2"
                      >
                        {displayAlert(a)}
                      </Typography>
                    )}
                    secondary={(
                      <Typography
                        className={a.isWarning ? 'warning' : 'error'}
                        variant="caption"
                      >
                        {a.alertTime && format(a.alertTime, 'PPp')}
                      </Typography>
                    )}
                  />
                </Link>
              </ListItem>))
            }
          </AlertList> :
          <NoAlerts />
        }
        {alertChunks.length > 1 &&
          <Pagination 
            count={Math.ceil(alertCount / pageSize)} 
            page={page}
            defaultPage={1}
            sx={{ mb: -2 }} 
            onChange={handleChange} 
          />        
        }
      </CardContent>
    </Card>
  )
}

export default EncryptionAlertsCard;

